/**
 * Preview dev-only de /tesoreria/casas (Sprint 17 Fase 2).
 *
 * Mock data plausible para una vista global del barrio (~60 casas
 * representadas como 15 para densidad razonable). Estados: al_dia,
 * al_dia_pendiente_semestre, atrasada, suspendida. La compensación NO es
 * un estado (rediseño 2026-06-25): se ve como "C" en los meses y como el
 * flag `tiene_compensacion` (filtro). Incluye un caso de compensación
 * total y uno parcial (meses C + meses pagados).
 */
import { CasasView } from '../tesoreria/Casas';
import type {
  CasaAnualResumen,
  CasasAnualResponse,
  EstadoMes,
} from '../../lib/api/casas-tesoreria';

function mockCasa(
  identificador: string,
  titular: string,
  estado: CasaAnualResumen['estado'],
  saldo: number,
  saldo_favor: number,
  ultimo_pago: string | null,
  meses: EstadoMes[],
  tieneComp = false,
): CasaAnualResumen {
  return {
    lote_id: `lote-${identificador}`,
    identificador,
    // Sprint 17 Z16 — mock alternando sector para que el preview muestre
    // tanto Oriente como Poniente.
    sector:
      ['Oriente', 'Poniente'][
        Number.parseInt(identificador.slice(-1), 10) % 2
      ] ?? 'Oriente',
    titular_nombre: titular,
    titular_celular:
      titular === 'Juan Pérez' ? '912345678' : null,
    estado,
    saldo_total_pendiente: saldo,
    saldo_semestre_actual: saldo,
    saldo_a_favor: saldo_favor,
    saldo_a_hoy: saldo - saldo_favor,
    deuda_anio_anterior: saldo > 0 ? Math.floor(saldo / 2) : 0,
    abono_anio_anterior: saldo > 0 ? Math.floor(saldo / 4) : 0,
    ultimo_pago_fecha: ultimo_pago,
    meses,
    tiene_compensacion: tieneComp,
  };
}

// Mes actual = mayo (5). Patrones de pago segun escenario.
const MESES_AL_DIA: EstadoMes[] = [
  'pagado', 'pagado', 'pagado', 'pagado', 'pagado',
  'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro',
];

const MESES_ATRASADA: EstadoMes[] = [
  'pagado', 'pagado', 'no_pagado', 'no_pagado', 'no_pagado',
  'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro',
];

const MESES_SUSPENDIDA: EstadoMes[] = [
  'no_pagado', 'no_pagado', 'no_pagado', 'no_pagado', 'no_pagado',
  'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro',
];

// Compensación TOTAL del semestre (caso citofonía/cámaras: la casa no
// paga cuota, se le compensa completa).
const MESES_COMP: EstadoMes[] = [
  'compensacion', 'compensacion', 'compensacion', 'compensacion', 'compensacion',
  'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro',
];

// Compensación PARCIAL: 2 meses compensados por algo puntual, el resto
// pagado por transferencia. Estado de la casa = al día.
const MESES_COMP_PARCIAL: EstadoMes[] = [
  'pagado', 'pagado', 'compensacion', 'compensacion', 'pagado',
  'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro', 'futuro',
];

const CASAS_MOCK: CasaAnualResumen[] = [
  mockCasa('6834', 'María González', 'al_dia', 0, 0, '2026-05-10', MESES_AL_DIA),
  mockCasa('6846', 'Juan Pérez', 'al_dia', 0, 5000, '2026-05-12', MESES_AL_DIA),
  mockCasa('6911', 'Pedro Soto', 'atrasada', 15000, 0, '2026-02-15', MESES_ATRASADA),
  mockCasa('6913', 'Ana Vergara', 'atrasada', 30000, 0, '2026-02-28', MESES_ATRASADA),
  mockCasa('6931', 'Diego Ramos', 'al_dia', 0, 0, '2026-05-08', MESES_AL_DIA),
  mockCasa('6947', 'Carolina Méndez', 'al_dia', 0, 0, '2026-05-15', MESES_COMP, true),
  mockCasa('6951', 'Felipe Soto', 'atrasada', 90000, 0, '2025-12-20', MESES_SUSPENDIDA),
  mockCasa('6954', 'Cristina Vidal', 'suspendida', 120000, 0, '2025-11-05', MESES_SUSPENDIDA),
  mockCasa('6962', 'Hugo Cárdenas', 'al_dia', 0, 0, '2026-05-01', MESES_AL_DIA),
  mockCasa('6975', 'Luisa Fernández', 'al_dia', 0, 2500, '2026-04-28', MESES_AL_DIA),
  mockCasa('6991', 'Sergio Salinas', 'atrasada', 10000, 0, '2026-04-15', MESES_ATRASADA),
  mockCasa('7001', 'Patricia Reyes', 'al_dia', 0, 0, '2026-05-03', MESES_COMP_PARCIAL, true),
  mockCasa('7012', 'Ricardo Torres', 'al_dia', 0, 0, '2026-05-09', MESES_AL_DIA),
  mockCasa('7023', 'Valeria Carrasco', 'atrasada', 5000, 0, '2026-04-10', MESES_ATRASADA),
  mockCasa('7034', 'Miguel Bahamonde', 'al_dia', 0, 0, '2026-05-14', MESES_AL_DIA),
];

const RESPONSE_MOCK: CasasAnualResponse = {
  anio: new Date().getFullYear(),
  casas: CASAS_MOCK,
  conteos: {
    al_dia: CASAS_MOCK.filter((c) => c.estado === 'al_dia').length,
    atrasada: CASAS_MOCK.filter((c) => c.estado === 'atrasada').length,
    suspendida: CASAS_MOCK.filter((c) => c.estado === 'suspendida').length,
    al_dia_pendiente_semestre: CASAS_MOCK.filter(
      (c) => c.estado === 'al_dia_pendiente_semestre',
    ).length,
    // "compensacion" cuenta casas con el flag, no un estado.
    compensacion: CASAS_MOCK.filter((c) => c.tiene_compensacion).length,
  },
};

const SIDEBAR_ITEMS = [
  { label: 'Inicio' },
  { label: 'Casas', active: true },
  { label: 'Pagos' },
  { label: 'Banco' },
  { label: 'Ingresos varios', proximamente: true },
  { label: 'Gastos', proximamente: true },
  { label: 'Compensaciones' },
  { label: 'Recursos' },
  { label: 'Cuotas', proximamente: true },
  { label: 'Suspensiones', proximamente: true },
  { label: 'Cierres', proximamente: true },
  { label: 'Reportes', proximamente: true },
];

export function TesoreriaCasasPreview() {
  return (
    <div className="min-h-screen bg-bg-page text-text-primary flex">
      <aside className="hidden md:flex w-64 bg-bg-sunken border-r-[0.5px] border-white/[0.08] flex-col">
        <div className="px-6 py-5 border-b-[0.5px] border-white/[0.08]">
          <div className="text-xs uppercase tracking-widest text-orange font-medium">
            Tesorería
          </div>
          <div className="text-base text-text-primary font-medium mt-1">
            Barrio Calbucura
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {SIDEBAR_ITEMS.map((it) => (
            <div
              key={it.label}
              className={`flex items-center justify-between px-3 py-2 rounded text-sm ${
                it.active
                  ? 'bg-bg-card text-text-primary font-medium'
                  : 'text-text-secondary'
              } ${it.proximamente ? 'opacity-70' : ''}`}
            >
              <span>{it.label}</span>
              {it.proximamente && (
                <span className="text-[10px] uppercase tracking-wide text-text-muted">
                  pronto
                </span>
              )}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-bg-card border-b-[0.5px] border-white/[0.08] px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <div className="text-sm text-text-secondary">
            Panel del tesorero · Casas
          </div>
          <div className="text-xs text-text-muted">Preview dev-only</div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <CasasView data={RESPONSE_MOCK} loading={false} error={null} />
        </main>
      </div>
    </div>
  );
}
